import { useQuery } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type { ChargeableEntity } from '@erp/shared-types';

export interface UseChargeOptions {
  chargeId: string;
  enabled?: boolean;
}

/**
 * Hook to fetch a single charge by ID
 */
export function useCharge({ chargeId, enabled = true }: UseChargeOptions) {
  const query = useQuery({
    ...orpc.finance.charges.getById.queryOptions({
      input: { id: chargeId },
    }),
    enabled: enabled && !!chargeId,
  });

  return {
    ...query,
    charge: query.data?.charge ?? null,
  };
}

export interface UseChargesForEntityOptions {
  entityType: ChargeableEntity;
  entityId: string;
  headerDocId?: string | null;
  includeLineCharges?: boolean;
  enabled?: boolean;
}

/**
 * Hook to fetch charges for a specific entity (document or document line)
 */
export function useChargesForEntity({
  entityType,
  entityId,
  headerDocId,
  includeLineCharges = true,
  enabled = true,
}: UseChargesForEntityOptions) {
  const query = useQuery({
    ...orpc.finance.charges.getForEntity.queryOptions({
      input: {
        entity: {
          entityType,
          entityId,
        },
        headerDocId: headerDocId ?? undefined,
        includeLineCharges,
      },
    }),
    enabled: enabled && !!entityId,
  });

  return {
    ...query,
    charges: query.data?.charges ?? [],
  };
}
